import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AddBrandModal } from './AddBrandModal';
import { EditBrandModal } from './EditBrandModal';
import { useAllBrandMetrics } from '../hooks/useAllBrandMetrics';
import { useBrandCampaigns } from '../hooks/useBrandCampaigns';
import { useBrandMetrics } from '../hooks/useBrandMetrics';
import { useAuth } from '@/contexts/AuthContext';
import type { BrandWithMetrics } from '../types/brand.types';
import {
  BrandHeader,
  BrandListColumn,
  BrandDetailView,
  ImportBrandsModal,
} from '@/components/brands';

export const BrandManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [showOnlyActiveFilter, setShowOnlyActiveFilter] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [editBrandOpen, setEditBrandOpen] = useState<boolean>(false);
  const [addBrandOpen, setAddBrandOpen] = useState<boolean>(false);
  const [importBrandsOpen, setImportBrandsOpen] = useState<boolean>(false);

  const { data: allBrands, isLoading, error, refetch } = useAllBrandMetrics(showOnlyActiveFilter);
  const { userCountry } = useAuth();
  const navigate = useNavigate();

  // Filter brands based on search term
  const brands = useMemo(() => {
    if (!allBrands || !searchTerm.trim()) {
      return allBrands || [];
    }

    return allBrands.filter((brand) =>
      brand.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [allBrands, searchTerm]);

  // Derived state: select first brand by default when brands are fetched
  const [selectedBrand, setSelectedBrand] = useState<BrandWithMetrics | null>(null);
  useEffect(() => {
    if (brands && brands.length > 0 && (!selectedBrand || !brands.find((b) => b.id === selectedBrand.id))) {
      setSelectedBrand(brands[0]);
    }
  }, [brands, selectedBrand]);

  // Fetch data for selected brand
  const { data: brandCampaigns, isLoading: loadingCampaigns } = useBrandCampaigns(
    selectedBrand?.id || '',
    selectedBrand?.name
  );
  const { data: brandMetrics, isLoading: loadingMetrics } = useBrandMetrics(
    selectedBrand?.id || '',
    selectedBrand?.name
  );

  const handleViewCampaigns = () => {
    navigate('/campaigns', { state: { filteredBrandId: selectedBrand?.id } });
  };

  const handleCampaignClick = (campaignId: string) => {
    navigate(`/campaigns/${campaignId}`);
  };

  const handleEditBrand = () => {
    setEditBrandOpen(true);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const handleImportSuccess = () => {
    refetch();
  };

  return (
    <div className="h-screen flex flex-col p-6 overflow-hidden">
      <AddBrandModal
        open={addBrandOpen}
        setOpen={setAddBrandOpen}
        existingBrands={allBrands || []}
      />
      <ImportBrandsModal
        open={importBrandsOpen}
        onOpenChange={setImportBrandsOpen}
        onImported={handleImportSuccess}
      />
      {selectedBrand && (
        <EditBrandModal
          key={selectedBrand.id}
          open={editBrandOpen}
          setOpen={setEditBrandOpen}
          brand={selectedBrand}
          onSuccess={() => {
            refetch();
          }}
        />
      )}

      {/* Page Header */}
      <BrandHeader
        showOnlyActiveFilter={showOnlyActiveFilter}
        onToggleActiveFilter={() => setShowOnlyActiveFilter(!showOnlyActiveFilter)}
        onOpenImport={() => setImportBrandsOpen(true)}
        onOpenAdd={() => setAddBrandOpen(true)}
      />

      {isLoading && <div className="text-center py-24 text-gray-400">Loading brands…</div>}
      {error && <div className="text-center py-24 text-red-600">Error loading brands.</div>}

      {allBrands && allBrands.length > 0 && (
        <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Brand List Column */}
          <BrandListColumn
            brands={brands}
            selectedBrand={selectedBrand}
            onSelectBrand={setSelectedBrand}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            onClearSearch={clearSearch}
            showOnlyActiveFilter={showOnlyActiveFilter}
            userCountry={userCountry}
          />

          {/* Right Column (Detail View) */}
          {selectedBrand && (
            <BrandDetailView
              selectedBrand={selectedBrand}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              onEditBrand={handleEditBrand}
              brandMetrics={brandMetrics}
              loadingMetrics={loadingMetrics}
              brandCampaigns={brandCampaigns}
              loadingCampaigns={loadingCampaigns}
              userCountry={userCountry}
              onViewCampaigns={handleViewCampaigns}
              onCampaignClick={handleCampaignClick}
              navigate={navigate}
            />
          )}
        </div>
      )}

      {allBrands && allBrands.length === 0 && !isLoading && !error && (
        <div className="text-center py-24 text-gray-500">
          {showOnlyActiveFilter
            ? 'No brands found with active campaigns.'
            : 'No brands found. Add your first brand!'}
        </div>
      )}
    </div>
  );
};

export default BrandManagement;
